import { useEffect, useState, useCallback } from "react";
import { api, extractError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Switch } from "../../components/ui/switch";
import { Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["worker", "accounting", "admin"];
const NO_DEPT = "__none__";
const NO_COMPANY = "__none__";

function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

const EMPTY = { email: "", password: "", name: "", role: "worker", active: true, default_department_id: NO_DEPT, default_company_id: NO_COMPANY };

export default function UsersAdmin() {
  const { user: currentUser, reload: reloadMe } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [u, d, co] = await Promise.all([
        api.get("/users", { params: { active_only: false } }),
        api.get("/departments/active"),
        api.get("/companies/active"),
      ]);
      setUsers(u.data);
      setDepartments(d.data);
      setCompanies(co.data);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const deptById = Object.fromEntries(departments.map((d) => [d.id, d]));
  const companyById = Object.fromEntries(companies.map((c) => [c.id, c]));
  const formatDept = (id) => {
    if (!id) return "—";
    const d = deptById[id];
    return d ? `${d.name} (${d.code})` : "—";
  };
  const formatCompany = (id) => {
    if (!id) return "—";
    const c = companyById[id];
    return c ? `${c.name} (${c.code})` : "—";
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setFormError("");
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      password: "",
      name: u.name,
      role: u.role,
      active: u.active,
      default_department_id: u.default_department_id || NO_DEPT,
      default_company_id: u.default_company_id || NO_COMPANY,
    });
    setFormError("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");
    try {
      const deptVal = form.default_department_id === NO_DEPT ? null : form.default_department_id;
      const companyVal = form.default_company_id === NO_COMPANY ? null : form.default_company_id;
      if (editing) {
        const payload = {
          email: form.email,
          name: form.name,
          role: form.role,
          active: form.active,
          default_department_id: deptVal,
          default_company_id: companyVal,
        };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("User updated");
        // If admin edited their own email, refresh /auth/me so the navbar
        // dropdown header reflects the new address without a re-login. The
        // JWT itself stays valid (email is purely display; the sub claim is
        // the immutable user id).
        if (currentUser && editing.id === currentUser.id && form.email !== editing.email) {
          await reloadMe();
        }
      } else {
        await api.post("/users", {
          email: form.email,
          password: form.password,
          name: form.name,
          role: form.role,
          active: form.active,
          default_department_id: deptVal,
          default_company_id: companyVal,
        });
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      setFormError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deactivated");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <div data-testid="users-admin">
      <PageHeader
        title="Users"
        subtitle="Manage workers, accounting, and admins."
        action={
          <Button onClick={openCreate} data-testid="create-user-btn" className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            <Plus className="mr-1 h-4 w-4" /> New user
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4" data-testid="users-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="rounded-md border border-slate-200 bg-white">
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Default Department</TableHead>
              <TableHead>Default Company</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500">Loading…</TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500">No users</TableCell></TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium">{u.email}</TableCell>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="bg-[#0F1E3D]/5 text-[#0F1E3D]">{u.role}</Badge>
                  </TableCell>
                  <TableCell className="text-slate-600 text-xs" data-testid={`user-default-dept-${u.email}`}>
                    {formatDept(u.default_department_id)}
                  </TableCell>
                  <TableCell className="text-slate-600 text-xs" data-testid={`user-default-company-${u.email}`}>
                    {formatCompany(u.default_company_id)}
                  </TableCell>
                  <TableCell>
                    {u.active ? (
                      <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Active</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-slate-100 text-slate-600">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {u.active && (
                      <Button variant="ghost" size="sm" onClick={() => deactivate(u)} data-testid={`deactivate-user-${u.email}`}>
                        <Power className="h-4 w-4 text-rose-600" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit user" : "New user"}</DialogTitle>
              <DialogDescription>
                {editing ? "Update name, role, status, default department, or set a new password." : "Create a new account."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="u-email">Email</Label>
              <Input id="u-email" type="email" required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="user-email-input" />
              {editing && (
                <p className="text-xs text-slate-500">
                  Renaming sends a PATCH; another user with this email will return a 409.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="u-name">Name</Label>
              <Input id="u-name" required value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="user-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="u-password">{editing ? "New password (optional)" : "Password"}</Label>
              <Input id="u-password" type="password" required={!editing} minLength={editing ? 0 : 6}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                data-testid="user-password-input" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Default department (optional)</Label>
              <Select value={form.default_department_id} onValueChange={(v) => setForm((f) => ({ ...f, default_department_id: v }))}>
                <SelectTrigger data-testid="user-default-dept-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DEPT}>— None —</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`user-default-dept-opt-${d.code}`}>
                      {d.code} — {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Pre-fills the department on this user's expense entries.</p>
            </div>
            <div className="space-y-2">
              <Label>Default company (optional)</Label>
              <Select value={form.default_company_id} onValueChange={(v) => setForm((f) => ({ ...f, default_company_id: v }))}>
                <SelectTrigger data-testid="user-default-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_COMPANY}>— None —</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`user-default-company-opt-${c.code}`}>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Pre-fills the company on this user's expense entries.</p>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-slate-500">Deactivated users cannot sign in.</p>
              </div>
              <Switch checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                data-testid="user-active-switch" />
            </div>

            {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="user-submit-btn"
                className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                {submitting ? "Saving…" : editing ? "Save changes" : "Create user"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
